import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import useSubmitGuard from '../hooks/useSubmitGuard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  AlertTriangle, Plus, RefreshCw, Wrench, RotateCcw, Trash2, Eye, CircleOff, CheckCircle2,
} from 'lucide-react';
import DeleteRequestDialog from '../components/DeleteRequestDialog';
import PendingDeletionsPanel from '../components/PendingDeletionsPanel';
import SearchableSelect from '../components/SearchableSelect';
import { 
  FilterBar, 
  SelectFilter, 
  SearchFilter,
  countActiveFilters 
} from '../components/FilterComponents';
import { ExportButton } from '../utils/exportUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  in_stock: 'bg-green-100 text-green-800',
  fitted: 'bg-blue-100 text-blue-800',
  sent_for_rebuttoning: 'bg-amber-100 text-amber-800',
  scrapped: 'bg-red-100 text-red-800',
};
const statusLabels = {
  in_stock: 'In Stock', fitted: 'Fitted', sent_for_rebuttoning: 'Rebuttoning', scrapped: 'Scrapped',
};

function TyreManagementPage({ user }) {
  const canApprove = user?.is_admin || user?.is_super_admin;
  const guard = useSubmitGuard();

  // Delete approval
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pendingDelRequests, setPendingDelRequests] = useState([]);

  const fetchPendingDeletions = async () => {
    if (!canApprove) return;
    try {
      const t = localStorage.getItem('token');
      const res = await axios.get(`${API}/delete-requests?status=pending&module=tyres`, {
        headers: { Authorization: `Bearer ${t}` }
      });
      setPendingDelRequests(res.data);
    } catch {}
  };

  const handleApproveDelete = async (reqId) => {
    try {
      const t = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/approve`, {}, { headers: { Authorization: `Bearer ${t}` } });
      toast.success('Deletion approved');
      fetchAll();
      fetchPendingDeletions();
    } catch { toast.error('Failed to approve'); }
  };

  const handleRejectDelete = async (reqId) => {
    try {
      const t = localStorage.getItem('token');
      await axios.post(`${API}/delete-requests/${reqId}/reject`, { note: 'Rejected' }, { headers: { Authorization: `Bearer ${t}` } });
      toast.success('Request rejected');
      fetchPendingDeletions();
    } catch { toast.error('Failed to reject'); }
  };

  const handleDeleteTyre = async (tyre) => {
    if (canApprove) {
      if (!window.confirm(`Delete tyre ${tyre.serial_number}?`)) return;
      try {
        const t = localStorage.getItem('token');
        await axios.delete(`${API}/tyres/${tyre.id}`, { headers: { Authorization: `Bearer ${t}` } });
        toast.success('Tyre deleted');
        fetchAll();
        fetchPendingDeletions();
      } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete tyre'); }
    } else {
      setDeleteTarget({ id: tyre.id, display: `Tyre: ${tyre.serial_number} (${tyre.brand} ${tyre.size})` });
    }
  };

  const [activeTab, setActiveTab] = useState('inventory');
  const [tyres, setTyres] = useState([]);
  const [fittings, setFittings] = useState([]);
  const [rebuttoning, setRebuttoning] = useState([]);
  const [scrapTyres, setScrapTyres] = useState([]);
  const [stats, setStats] = useState({});
  const [vehicleClasses, setVehicleClasses] = useState({});
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    vehicle: ''
  });

  const resetFilters = () => {
    setFilters({ search: '', status: '', vehicle: '' });
  };

  // Dynamic positions for the selected vehicle in fitting form
  const [fitPositions, setFitPositions] = useState([]);
  const [fitDetectedClass, setFitDetectedClass] = useState('');
  const [positionMode, setPositionMode] = useState('predefined'); // 'predefined' | 'custom'

  // Modal states
  const [showAddTyre, setShowAddTyre] = useState(false);
  const [showFitTyre, setShowFitTyre] = useState(false);
  const [showRemoveTyre, setShowRemoveTyre] = useState(false);
  const [showRebuttoning, setShowRebuttoning] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedTyre, setSelectedTyre] = useState(null);
  const [selectedFitting, setSelectedFitting] = useState(null);
  const [tyreHistory, setTyreHistory] = useState(null);

  const [newTyre, setNewTyre] = useState({ serial_number: '', brand: '', size: '', tyre_type: 'radial', recommended_hours: 2000 });
  const [fitForm, setFitForm] = useState({ tyre_id: '', vehicle_door_no: '', position: '', custom_position: '', fitted_at_hours: '', fitted_date: new Date().toISOString().split('T')[0] });
  const [removeForm, setRemoveForm] = useState({ removed_at_hours: '', removed_date: new Date().toISOString().split('T')[0], removal_reason: 'routine', action_after: 'stock' });
  const [rebuttonForm, setRebuttonForm] = useState({ tyre_id: '', sent_date: new Date().toISOString().split('T')[0], vendor_name: '', notes: '' });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); fetchPendingDeletions(); }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [tyresRes, fittingsRes, rebutRes, statsRes, posRes, assetsRes, scrapRes] = await Promise.all([
        axios.get(`${API}/tyres`, { headers }),
        axios.get(`${API}/tyre-fittings`, { headers }),
        axios.get(`${API}/tyre-rebuttoning`, { headers }),
        axios.get(`${API}/tyres/stats`, { headers }),
        axios.get(`${API}/tyres/positions`, { headers }),
        axios.get(`${API}/assets`, { headers }),
        axios.get(`${API}/scrap?item_type=tyre`, { headers }),
      ]);
      setTyres(tyresRes.data);
      setFittings(fittingsRes.data);
      setRebuttoning(rebutRes.data);
      setStats(statsRes.data);
      setVehicleClasses(posRes.data.all_classes || {});
      setAssets(assetsRes.data);
      setScrapTyres(scrapRes.data);
    } catch {
      toast.error('Failed to load tyre data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTyre = async () => {
    guard(async () => {
      try {
        await axios.post(`${API}/tyres`, newTyre, { headers });
        toast.success('Tyre added successfully');
        setShowAddTyre(false);
        setNewTyre({ serial_number: '', brand: '', size: '', tyre_type: 'radial', recommended_hours: 2000 });
        fetchAll();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to add tyre');
      }
    });
  };

  const handleFitTyre = async () => {
    const finalPosition = positionMode === 'custom' ? fitForm.custom_position : fitForm.position;
    if (!finalPosition) {
      toast.error('Please select or enter a tyre position');
      return;
    }
    guard(async () => {
      try {
        await axios.post(`${API}/tyre-fittings`, {
          tyre_id: fitForm.tyre_id,
          vehicle_door_no: fitForm.vehicle_door_no,
          position: finalPosition,
          fitted_at_hours: parseFloat(fitForm.fitted_at_hours),
          fitted_date: fitForm.fitted_date,
        }, { headers });
        toast.success('Tyre fitted successfully');
        setShowFitTyre(false);
        fetchAll();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to fit tyre');
      }
    });
  };

  const handleVehicleSelect = async (doorNo) => {
    setFitForm(f => ({ ...f, vehicle_door_no: doorNo, position: '' }));
    setFitPositions([]);
    setFitDetectedClass('');
    setPositionMode('predefined');
    if (!doorNo) return;
    try {
      const res = await axios.get(`${API}/tyres/positions?door_no=${encodeURIComponent(doorNo)}`, { headers });
      const positions = res.data.positions || [];
      setFitPositions(positions);
      setFitDetectedClass(res.data.detected_label || '');
      if (positions.length === 0) setPositionMode('custom');
    } catch {
      setPositionMode('custom');
    }
  };

  const handleRemoveTyre = async () => {
    guard(async () => {
      try {
        await axios.put(`${API}/tyre-fittings/${selectedFitting.id}/remove`, {
          removed_at_hours: parseFloat(removeForm.removed_at_hours),
          removed_date: removeForm.removed_date,
          removal_reason: removeForm.removal_reason,
          action_after: removeForm.action_after,
        }, { headers });
        toast.success('Tyre removed successfully');
        setShowRemoveTyre(false);
        fetchAll();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to remove tyre');
      }
    });
  };

  const handleSendRebuttoning = async () => {
    guard(async () => {
      try {
        await axios.post(`${API}/tyre-rebuttoning`, rebuttonForm, { headers });
        toast.success('Tyre sent for rebuttoning');
        setShowRebuttoning(false);
        fetchAll();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to send for rebuttoning');
      }
    });
  };

  const handleMarkReturned = async (rebutId) => {
    try {
      await axios.put(`${API}/tyre-rebuttoning/${rebutId}`, {
        returned_date: new Date().toISOString().split('T')[0],
        status: 'returned'
      }, { headers });
      toast.success('Tyre marked as returned');
      fetchAll();
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleViewHistory = async (tyre) => {
    try {
      const res = await axios.get(`${API}/tyres/${tyre.id}/history`, { headers });
      setTyreHistory(res.data);
      setShowHistory(true);
    } catch {
      toast.error('Failed to load history');
    }
  };

  const stockTyres = tyres.filter(t => t.status === 'in_stock');

  const tabs = [
    { id: 'inventory', label: 'Tyre Inventory' },
    { id: 'fittings', label: 'Fitting Log' },
    { id: 'rebuttoning', label: 'Rebuttoning Tracker' },
    { id: 'scrapped', label: 'Scrapped Tyres' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Tyre Management</h1>
          <p className="text-slate-500 text-sm mt-1">Track tyre lifecycle — fitting, rotation, rebuttoning & scrapping</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchAll}>
          <RefreshCw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Stats */}
      <PendingDeletionsPanel module="tyres" requests={pendingDelRequests} onApprove={handleApproveDelete} onReject={handleRejectDelete} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Tyres', value: stats.total || 0, color: 'text-slate-800' },
          { label: 'In Stock', value: stats.in_stock || 0, color: 'text-green-600' },
          { label: 'Fitted', value: stats.fitted || 0, color: 'text-blue-600' },
          { label: 'Rebuttoning', value: stats.sent_for_rebuttoning || 0, color: 'text-amber-600' },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="pt-4">
              <p className="text-xs text-slate-500">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Alert Banner */}
      {stats.alert_count > 0 && (
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          <span className="text-sm font-medium">{stats.alert_count} tyre(s) have exceeded recommended hours — consider rebuttoning or replacement.</span>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              activeTab === tab.id
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid={`tyre-tab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ========== INVENTORY TAB ========== */}
      {activeTab === 'inventory' && (
        <div className="space-y-4">
          {/* Filters */}
          <FilterBar 
            onReset={resetFilters} 
            activeFiltersCount={countActiveFilters(filters)}
          >
            <SearchFilter
              label="Search"
              value={filters.search}
              onChange={(val) => setFilters({ ...filters, search: val })}
              placeholder="Serial No, Make..."
            />
            <SelectFilter
              label="Status"
              value={filters.status}
              onChange={(val) => setFilters({ ...filters, status: val })}
              options={Object.entries(statusLabels).map(([k, v]) => ({ value: k, label: v }))}
            />
            <SelectFilter
              label="Fitted Vehicle"
              value={filters.vehicle}
              onChange={(val) => setFilters({ ...filters, vehicle: val })}
              options={[...new Set(fittings.map(f => f.asset_door_no))].map(v => ({ value: v, label: v }))}
            />
          </FilterBar>
          
          <div className="flex justify-end">
            <Button onClick={() => setShowAddTyre(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-2" />Add Tyre
            </Button>
          </div>
          
          {(() => {
            // Apply filters
            let filteredTyres = [...tyres];
            if (filters.search) {
              const searchLower = filters.search.toLowerCase();
              filteredTyres = filteredTyres.filter(t => 
                t.serial_number?.toLowerCase().includes(searchLower) ||
                t.make?.toLowerCase().includes(searchLower) ||
                t.model?.toLowerCase().includes(searchLower)
              );
            }
            if (filters.status) {
              filteredTyres = filteredTyres.filter(t => t.status === filters.status);
            }
            if (filters.vehicle) {
              const fittedTyreIds = fittings
                .filter(f => f.asset_door_no === filters.vehicle && !f.removed_date)
                .map(f => f.tyre_id);
              filteredTyres = filteredTyres.filter(t => fittedTyreIds.includes(t.id));
            }
            
            return (
              <Card>
                <CardContent className="p-0">
                  <div className="p-3 border-b flex justify-between items-center">
                    <span className="text-sm text-slate-500">{filteredTyres.length} tyres</span>
                    <ExportButton
                      rows={filteredTyres}
                      columns={[
                        { header: 'Serial No.', key: 'serial_number' },
                        { header: 'Brand', key: 'brand' },
                        { header: 'Size', key: 'size' },
                        { header: 'Status', key: 'status' },
                        { header: 'Fitted On', key: 'fitted_on_door_no' },
                        { header: 'Position', key: 'position' },
                        { header: 'KMs Run', key: 'current_km' },
                        { header: 'Purchase Date', key: 'purchase_date' },
                        { header: 'Price', key: 'purchase_price' },
                      ]}
                      filename={`tyres_${new Date().toISOString().slice(0,10)}`}
                      title="Tyre Inventory Report"
                      subtitle={`Exported on ${new Date().toLocaleDateString('en-IN')}`}
                    />
                  </div>
                  <Table>
                    <TableHeader>
                  <TableRow>
                    <TableHead>Serial #</TableHead>
                    <TableHead>Brand / Size</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Current Vehicle</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead className="text-right">Total Hours</TableHead>
                    <TableHead className="text-right">Rec. Hours</TableHead>
                    <TableHead>Rebuttoning</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow>
                  ) : filteredTyres.length === 0 ? (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-slate-400">No tyres match the filters</TableCell></TableRow>
                  ) : filteredTyres.map(tyre => (
                    <TableRow key={tyre.id} className={tyre.is_alert ? 'bg-amber-50' : ''}>
                      <TableCell className="font-mono font-medium">
                        {tyre.serial_number}
                        {tyre.is_alert && <AlertTriangle className="h-3 w-3 text-amber-500 inline ml-1" />}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{tyre.brand}</div>
                        <div className="text-xs text-slate-500">{tyre.size}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[tyre.status] || 'bg-slate-100'}>
                          {statusLabels[tyre.status] || tyre.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{tyre.current_vehicle_id || '—'}</TableCell>
                      <TableCell>{tyre.current_position || '—'}</TableCell>
                      <TableCell className={`text-right font-medium ${tyre.is_alert ? 'text-amber-600' : ''}`}>
                        {tyre.total_hours_used?.toFixed(0) || 0}h
                      </TableCell>
                      <TableCell className="text-right text-slate-500">{tyre.recommended_hours}h</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{tyre.rebuttoning_count}x</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewHistory(tyre)} title="View History">
                            <Eye className="h-3 w-3" />
                          </Button>
                          {tyre.status === 'in_stock' && (
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedTyre(tyre); setFitForm(f => ({ ...f, tyre_id: tyre.id })); setFitPositions([]); setFitDetectedClass(''); setPositionMode('predefined'); setShowFitTyre(true); }} title="Fit Tyre">
                              <Wrench className="h-3 w-3" />
                            </Button>
                          )}
                          {tyre.status === 'in_stock' && (
                            <Button variant="ghost" size="sm" onClick={() => { setRebuttonForm(f => ({ ...f, tyre_id: tyre.id })); setShowRebuttoning(true); }} title="Send for Rebuttoning">
                              <RotateCcw className="h-3 w-3" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTyre(tyre)}
                            className="text-red-500 hover:text-red-700" title="Delete Tyre"
                            data-testid={`delete-tyre-${tyre.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
            );
          })()}
        </div>
      )}

      {/* ========== FITTING LOG TAB ========== */}
      {activeTab === 'fittings' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => { setFitPositions([]); setFitDetectedClass(''); setPositionMode('predefined'); setShowFitTyre(true); }} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-2" />Fit Tyre
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Serial #</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Fitted Date</TableHead>
                    <TableHead className="text-right">Fitted at (hrs)</TableHead>
                    <TableHead>Removed Date</TableHead>
                    <TableHead className="text-right">Hours Used</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fittings.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">No fitting records</TableCell></TableRow>
                  ) : fittings.map(f => (
                    <TableRow key={f.id}>
                      <TableCell className="font-mono font-medium">{f.tyre_serial}</TableCell>
                      <TableCell className="font-medium">{f.vehicle_door_no}</TableCell>
                      <TableCell>{f.position}</TableCell>
                      <TableCell>{f.fitted_date}</TableCell>
                      <TableCell className="text-right">{f.fitted_at_hours}h</TableCell>
                      <TableCell>{f.removed_date || '—'}</TableCell>
                      <TableCell className="text-right">{f.hours_used != null ? `${f.hours_used}h` : '—'}</TableCell>
                      <TableCell className="text-sm text-slate-500 capitalize">{f.removal_reason || '—'}</TableCell>
                      <TableCell>
                        <Badge className={f.is_active ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'}>
                          {f.is_active ? 'Active' : 'Removed'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {f.is_active && (
                          <Button variant="outline" size="sm" onClick={() => { setSelectedFitting(f); setShowRemoveTyre(true); }}>
                            Remove
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== REBUTTONING TAB ========== */}
      {activeTab === 'rebuttoning' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-600">
              <span className="font-medium">{rebuttoning.filter(r => r.status === 'sent').length}</span> tyre(s) currently out for rebuttoning
            </p>
            <Button onClick={() => setShowRebuttoning(true)} className="bg-slate-900 hover:bg-slate-800">
              <Plus className="h-4 w-4 mr-2" />Send for Rebuttoning
            </Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Serial #</TableHead>
                    <TableHead>Session #</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Sent Date</TableHead>
                    <TableHead>Sent By</TableHead>
                    <TableHead>Return Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rebuttoning.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">No rebuttoning records</TableCell></TableRow>
                  ) : rebuttoning.map(r => (
                    <TableRow key={r.id} className={r.status === 'sent' ? 'bg-amber-50' : ''}>
                      <TableCell className="font-mono font-medium">{r.tyre_serial}</TableCell>
                      <TableCell>
                        <Badge variant="outline">#{r.rebuttoning_number}</Badge>
                      </TableCell>
                      <TableCell>{r.vendor_name || '—'}</TableCell>
                      <TableCell>{r.sent_date}</TableCell>
                      <TableCell>{r.sent_by}</TableCell>
                      <TableCell>{r.returned_date || '—'}</TableCell>
                      <TableCell>
                        <Badge className={r.status === 'sent' ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}>
                          {r.status === 'sent' ? 'Out for Rebuttoning' : 'Returned'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {r.status === 'sent' && (
                          <Button variant="outline" size="sm" onClick={() => handleMarkReturned(r.id)}>
                            <CheckCircle2 className="h-3 w-3 mr-1" />Mark Returned
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ========== SCRAPPED TYRES TAB ========== */}
      {activeTab === 'scrapped' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CircleOff className="h-5 w-5 text-red-500" />
              Scrapped Tyres ({scrapTyres.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Serial #</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Scrapped Date</TableHead>
                  <TableHead>Weight (kg)</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Logged By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scrapTyres.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">No scrapped tyres. Add entries via the Scrap module.</TableCell></TableRow>
                ) : scrapTyres.map(s => (
                  <TableRow key={s.id}>
                    <TableCell className="font-mono font-medium">{s.serial_number || '—'}</TableCell>
                    <TableCell>{s.description}</TableCell>
                    <TableCell>{s.scrapped_date}</TableCell>
                    <TableCell>{s.weight_kg != null ? `${s.weight_kg} kg` : '—'}</TableCell>
                    <TableCell className="text-sm text-slate-500">{s.notes || '—'}</TableCell>
                    <TableCell>{s.logged_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* ===== ADD TYRE MODAL ===== */}
      <Dialog open={showAddTyre} onOpenChange={setShowAddTyre}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Tyre</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Serial Number *</Label>
                <Input placeholder="e.g. TYR-2024-001" value={newTyre.serial_number} onChange={e => setNewTyre(p => ({ ...p, serial_number: e.target.value }))} data-testid="tyre-serial-input" />
              </div>
              <div className="space-y-1">
                <Label>Brand *</Label>
                <Input placeholder="e.g. Michelin, Bridgestone" value={newTyre.brand} onChange={e => setNewTyre(p => ({ ...p, brand: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Size *</Label>
                <Input placeholder="e.g. 315/80R22.5" value={newTyre.size} onChange={e => setNewTyre(p => ({ ...p, size: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={newTyre.tyre_type} onValueChange={v => setNewTyre(p => ({ ...p, tyre_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="radial">Radial</SelectItem>
                    <SelectItem value="bias">Bias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Recommended Hours (for rebuttoning alert)</Label>
                <Input type="number" value={newTyre.recommended_hours} onChange={e => setNewTyre(p => ({ ...p, recommended_hours: parseFloat(e.target.value) }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTyre(false)}>Cancel</Button>
            <Button onClick={handleAddTyre} className="bg-slate-900 hover:bg-slate-800" data-testid="add-tyre-submit">Add Tyre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== FIT TYRE MODAL ===== */}
      <Dialog open={showFitTyre} onOpenChange={setShowFitTyre}>
        <DialogContent>
          <DialogHeader><DialogTitle>Fit Tyre to Vehicle</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Select Tyre (In Stock)</Label>
              <SearchableSelect
                value={fitForm.tyre_id}
                onValueChange={v => setFitForm(p => ({ ...p, tyre_id: v }))}
                options={stockTyres.map(t => ({ value: t.id, label: `${t.serial_number} — ${t.brand} ${t.size}` }))}
                placeholder="Select tyre"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 col-span-2">
                <Label>Vehicle (Door No.)</Label>
                <SearchableSelect
                  value={fitForm.vehicle_door_no}
                  onValueChange={handleVehicleSelect}
                  options={assets.map(a => ({ value: a.door_no, label: `${a.door_no} — ${a.make} ${a.model} (${a.asset_type})` }))}
                  placeholder="Select vehicle"
                />
                {fitDetectedClass && (
                  <p className="text-xs text-slate-500 mt-1">Detected: {fitDetectedClass}</p>
                )}
              </div>
              <div className="space-y-1 col-span-2">
                <div className="flex items-center justify-between">
                  <Label>Tyre Position</Label>
                  <button
                    type="button"
                    onClick={() => setPositionMode(m => m === 'predefined' ? 'custom' : 'predefined')}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {positionMode === 'predefined' ? 'Type custom position' : 'Use standard list'}
                  </button>
                </div>
                {positionMode === 'predefined' && fitPositions.length > 0 ? (
                  <Select value={fitForm.position} onValueChange={v => setFitForm(p => ({ ...p, position: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger>
                    <SelectContent>
                      {fitPositions.map(pos => (
                        <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="e.g. Front-Left, Rear-Right-Outer, Spare…"
                    value={positionMode === 'custom' ? fitForm.custom_position : fitForm.position}
                    onChange={e => {
                      const val = e.target.value;
                      if (positionMode === 'custom') setFitForm(p => ({ ...p, custom_position: val }));
                      else setFitForm(p => ({ ...p, position: val }));
                    }}
                  />
                )}
                {!fitForm.vehicle_door_no && (
                  <p className="text-xs text-slate-400">Select a vehicle first to auto-load standard positions</p>
                )}
              </div>
              <div className="space-y-1">
                <Label>Hour Meter at Fitting</Label>
                <Input type="number" placeholder="e.g. 12450" value={fitForm.fitted_at_hours} onChange={e => setFitForm(p => ({ ...p, fitted_at_hours: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Fitting Date</Label>
                <Input type="date" value={fitForm.fitted_date} onChange={e => setFitForm(p => ({ ...p, fitted_date: e.target.value }))}
                  min={!user?.is_super_admin ? new Date().toISOString().split('T')[0] : undefined}
                  max={!user?.is_super_admin ? new Date().toISOString().split('T')[0] : undefined}
                />
                {!user?.is_super_admin && <p className="text-xs text-amber-600 mt-1">Only today's date allowed</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFitTyre(false)}>Cancel</Button>
            <Button onClick={handleFitTyre} className="bg-slate-900 hover:bg-slate-800" data-testid="fit-tyre-submit">Fit Tyre</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== REMOVE TYRE MODAL ===== */}
      <Dialog open={showRemoveTyre} onOpenChange={setShowRemoveTyre}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Tyre: {selectedFitting?.tyre_serial}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Hour Meter at Removal</Label>
                <Input type="number" placeholder="e.g. 14500" value={removeForm.removed_at_hours} onChange={e => setRemoveForm(p => ({ ...p, removed_at_hours: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Removal Date</Label>
                <Input type="date" value={removeForm.removed_date} onChange={e => setRemoveForm(p => ({ ...p, removed_date: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label>Reason for Removal</Label>
                <Select value={removeForm.removal_reason} onValueChange={v => setRemoveForm(p => ({ ...p, removal_reason: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine Rotation</SelectItem>
                    <SelectItem value="puncture">Puncture</SelectItem>
                    <SelectItem value="damage">Damage</SelectItem>
                    <SelectItem value="wear">Wear Limit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Action After Removal</Label>
                <Select value={removeForm.action_after} onValueChange={v => setRemoveForm(p => ({ ...p, action_after: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock">Return to Stock</SelectItem>
                    <SelectItem value="rebuttoning">Send for Rebuttoning</SelectItem>
                    <SelectItem value="scrap">Scrap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedFitting && (
              <p className="text-sm text-slate-500">
                Fitted at {selectedFitting.fitted_at_hours}h on {selectedFitting.fitted_date} on {selectedFitting.vehicle_door_no} ({selectedFitting.position})
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveTyre(false)}>Cancel</Button>
            <Button onClick={handleRemoveTyre} className="bg-slate-900 hover:bg-slate-800" data-testid="remove-tyre-submit">Confirm Removal</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== REBUTTONING MODAL ===== */}
      <Dialog open={showRebuttoning} onOpenChange={setShowRebuttoning}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Tyre for Rebuttoning</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Select Tyre</Label>
              <SearchableSelect
                value={rebuttonForm.tyre_id}
                onValueChange={v => setRebuttonForm(p => ({ ...p, tyre_id: v }))}
                options={tyres.filter(t => t.status === 'in_stock').map(t => ({ value: t.id, label: `${t.serial_number} — ${t.brand} (${t.total_hours_used?.toFixed(0)}h / ${t.recommended_hours}h) ${t.is_alert ? '⚠' : ''}` }))}
                placeholder="Select tyre"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Sent Date</Label>
                <Input type="date" value={rebuttonForm.sent_date} onChange={e => setRebuttonForm(p => ({ ...p, sent_date: e.target.value }))}
                  min={!user?.is_super_admin ? new Date().toISOString().split('T')[0] : undefined}
                  max={!user?.is_super_admin ? new Date().toISOString().split('T')[0] : undefined}
                />
                {!user?.is_super_admin && <p className="text-xs text-amber-600 mt-1">Only today's date allowed</p>}
              </div>
              <div className="space-y-1">
                <Label>Vendor / Workshop</Label>
                <Input placeholder="Workshop name" value={rebuttonForm.vendor_name} onChange={e => setRebuttonForm(p => ({ ...p, vendor_name: e.target.value }))} />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Notes</Label>
                <Input placeholder="Any notes about condition" value={rebuttonForm.notes} onChange={e => setRebuttonForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRebuttoning(false)}>Cancel</Button>
            <Button onClick={handleSendRebuttoning} className="bg-slate-900 hover:bg-slate-800" data-testid="rebuttoning-submit">Send for Rebuttoning</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== HISTORY MODAL ===== */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Tyre History: {tyreHistory?.tyre?.serial_number}
            </DialogTitle>
          </DialogHeader>
          {tyreHistory && (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              <div className="flex gap-4 text-sm text-slate-600">
                <span><strong>Brand:</strong> {tyreHistory.tyre.brand}</span>
                <span><strong>Size:</strong> {tyreHistory.tyre.size}</span>
                <span><strong>Total Hours:</strong> {tyreHistory.tyre.total_hours_used}h</span>
                <span><strong>Rebuttoning:</strong> {tyreHistory.tyre.rebuttoning_count}x</span>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Fitting History ({tyreHistory.fittings.length})</h4>
                {tyreHistory.fittings.length === 0 ? (
                  <p className="text-sm text-slate-400">No fittings recorded</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead>Fitted</TableHead>
                        <TableHead>Removed</TableHead>
                        <TableHead>Hours Used</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tyreHistory.fittings.map(f => (
                        <TableRow key={f.id}>
                          <TableCell>{f.vehicle_door_no}</TableCell>
                          <TableCell>{f.position}</TableCell>
                          <TableCell className="text-xs">{f.fitted_date} @ {f.fitted_at_hours}h</TableCell>
                          <TableCell className="text-xs">{f.removed_date ? `${f.removed_date} @ ${f.removed_at_hours}h` : 'Still fitted'}</TableCell>
                          <TableCell>{f.hours_used != null ? `${f.hours_used}h` : '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Rebuttoning History ({tyreHistory.rebuttoning.length})</h4>
                {tyreHistory.rebuttoning.length === 0 ? (
                  <p className="text-sm text-slate-400">No rebuttoning recorded</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Session #</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Returned</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tyreHistory.rebuttoning.map(r => (
                        <TableRow key={r.id}>
                          <TableCell>#{r.rebuttoning_number}</TableCell>
                          <TableCell>{r.vendor_name || '—'}</TableCell>
                          <TableCell>{r.sent_date}</TableCell>
                          <TableCell>{r.returned_date || 'Pending'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeleteRequestDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        module="tyres"
        entryId={deleteTarget?.id}
        entryDisplay={deleteTarget?.display}
        onSuccess={() => setDeleteTarget(null)}
      />
    </div>
  );
}

export default TyreManagementPage;
